import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../config/firebase";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import {
  ArrowLeftRight,
  Bus,
  Building2,
  Loader2,
  MapPin,
  Menu,
  Navigation,
  Phone,
  X,
} from "lucide-react";
import CityAutocomplete from "../components/CityAutocomplete";
import "./HomeScreen.css";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";

// ===== Helpers Geográficos =====
const parseKm = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const toRad = (v) => (v * Math.PI) / 180;

const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * (Math.sin(dLng / 2) ** 2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
};

const minDistanceToPointsKm = (pathPoints, targetLatLng) => {
  if (!Array.isArray(pathPoints) || pathPoints.length === 0 || !targetLatLng) return Infinity;
  const tLat = targetLatLng.lat();
  const tLng = targetLatLng.lng();
  let best = Infinity;
  for (let i = 0; i < pathPoints.length; i++) {
    const p = pathPoints[i];
    const d = haversineKm(p.lat, p.lng, tLat, tLng);
    if (d < best) best = d;
  }
  return best;
};

// ✅ converte "HH:mm" em minutos para ordenar (menor -> maior)
const timeToMinutes = (t) => {
  if (!t) return 999999;
  const s = String(t).trim();
  const m = s.match(/(\d{1,2})\s*:\s*(\d{2})/);
  if (!m) return 999999;

  let hh = parseInt(m[1], 10);
  let mm = parseInt(m[2], 10);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return 999999;

  if (hh < 0) hh = 0;
  if (hh > 23) hh = 23;
  if (mm < 0) mm = 0;
  if (mm > 59) mm = 59;

  return hh * 60 + mm;
};

// Normaliza o campo placement
const normalizePlacement = (p, fallback = "side") => {
  const s = String(p || "").trim().toLowerCase();
  if (!s) return fallback;
  if (["top", "topo", "hero", "carrossel", "carousel", "top_banner", "banner_top", "banner-top"].includes(s)) return "top";
  if (["inline", "meio", "feed", "lista", "center", "centro"].includes(s)) return "inline";
  if (["left", "esquerda"].includes(s)) return "left";
  if (["right", "direita"].includes(s)) return "right";
  if (["side", "lateral", "laterais", "sidebar"].includes(s)) return "side";
  return s;
};

const getBannerImageUrl = (b) => b?.imageUrl || b?.bannerImageUrl || "";

// checa expiração (se existir)
const isBannerActive = (b) => {
  const exp = b?.expiresAt;
  if (!exp) return true;
  try {
    const d = exp?.toDate?.() || (exp instanceof Date ? exp : new Date(exp));
    if (!d || Number.isNaN(d.getTime())) return true;
    return d.getTime() > Date.now();
  } catch {
    return true;
  }
};

// -------------------------------
// Telefones (Motorista + Empresa)
// -------------------------------
const digitsOnly = (v) => String(v || "").replace(/\D/g, "");

const normCityKey = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (!s) return "";
  const base = s.split("-")[0].split(",")[0].trim();
  return base.normalize("NFD").replace(/\p{Diacritic}/gu, "");
};

const getDriverPhone = (r) => digitsOnly(r?.driverPhone || r?.phone || "");
const getOriginCompanyPhone = (r) => digitsOnly(r?.originCompanyPhone || "");
const getDestCompanyPhone = (r) => digitsOnly(r?.destCompanyPhone || "");

const pickCompanyPhoneForSearchOrigin = (r, searchOriginLabel) => {
  const so = normCityKey(searchOriginLabel);
  if (!so) return "";
  const ro = normCityKey(r?.origin);
  const rd = normCityKey(r?.destination);

  if (so && ro && so === ro) return getOriginCompanyPhone(r);
  if (so && rd && so === rd) return getDestCompanyPhone(r);

  return "";
};

// -------------------------------
// Taxa de embarque (opcional)
// -------------------------------
const getEmbarkFeeValue = (r) => {
  const n = Number(r?.embarkFeeValue ?? r?.taxaEmbarqueValue ?? r?.embarkFee ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
const hasEmbarkFee = (r) => r?.hasEmbarkFee === true || getEmbarkFeeValue(r) > 0;

// Aplica taxa somente quando busca começa na mesma cidade da origem da rota
const pickEmbarkFeeForSearchOrigin = (r, searchOriginLabel) => {
  if (!hasEmbarkFee(r)) return 0;
  const so = normCityKey(searchOriginLabel);
  const ro = normCityKey(r?.origin);
  if (!so || !ro) return 0;
  return so === ro ? getEmbarkFeeValue(r) : 0;
};

// -------------------------------
// Preço fixo por trecho (OVERRIDE)
// (Vem "copiado" para cada route no cadastro — assim Home não precisa ler /companies)
// -------------------------------
const pickOverrideFare = (route, originLabel, destLabel) => {
  const overrides = route?.companyFareOverrides || route?.fareOverrides || {};
  if (!overrides || typeof overrides !== "object") return null;

  const kAB = `${normCityKey(originLabel)}|${normCityKey(destLabel)}`;
  const kBA = `${normCityKey(destLabel)}|${normCityKey(originLabel)}`;

  const ovRaw = overrides[kAB] ?? overrides[kBA];
  const ov = Number(String(ovRaw ?? "").replace(",", "."));
  return Number.isFinite(ov) && ov > 0 ? ov : null;
};

export default function HomeScreen() {
  const navigate = useNavigate();

  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");

  const [addCidades, setAddCidades] = useState([]); // extras p/ autocomplete
  const [searchMeta, setSearchMeta] = useState({ origin: "", destination: "" });

  const [results, setResults] = useState([]);
  const [allRoutes, setAllRoutes] = useState([]);

  // ✅ Banners
  const [bannersTop, setBannersTop] = useState([]);
  const [bannersEsquerda, setBannersEsquerda] = useState([]);
  const [bannersDireita, setBannersDireita] = useState([]);

  const [loading, setLoading] = useState(false);
  const [isGeoLoading, setIsGeoLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const searchIdRef = useRef(0);

  // tolerância para aceitar origem/destino “em cima” da rota
  const ON_ROUTE_TOL_KM = 8;

  // ✅ pool de anúncios aleatórios (mobile) — sem duplicar
  const adsPool = (() => {
    const raw = [...(bannersEsquerda || []), ...(bannersDireita || [])].filter((b) => !!getBannerImageUrl(b));
    const seen = new Set();
    const out = [];
    for (const b of raw) {
      const k = String(b?.id || getBannerImageUrl(b) || "");
      if (!k) continue;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(b);
    }
    return out;
  })();

  // ✅ aplica geo na origem só se estiver vazio
  const applyGeoToOriginIfEmpty = (cityState) => {
    if (!cityState) return;
    setOrigin((prev) => (prev && String(prev).trim().length > 0 ? prev : cityState));
  };

  // ✅ Geolocalização ao montar
  useEffect(() => {
    const attemptGeo = () => {
      if ("geolocation" in navigator) handleGetLocation();
    };
    attemptGeo();
    const timer = setTimeout(attemptGeo, 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Geolocalização robusta
  const handleGetLocation = () => {
    if (!navigator.geolocation) return;

    const tryLowAccuracyFallback = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          if (window.google && window.google.maps) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (res, status) => {
              try {
                if (status === "OK" && res && res[0]) {
                  const comp = res[0].address_components || [];
                  const city =
                    comp.find((c) => c.types.includes("administrative_area_level_2"))?.long_name ||
                    comp.find((c) => c.types.includes("locality"))?.long_name ||
                    "";
                  const state = comp.find((c) => c.types.includes("administrative_area_level_1"))?.short_name || "";
                  if (city && state) applyGeoToOriginIfEmpty(`${city} - ${state}`);
                }
              } finally {
                setIsGeoLoading(false);
              }
            });
          } else {
            setIsGeoLoading(false);
          }
        },
        () => setIsGeoLoading(false),
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
      );
    };

    setIsGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (window.google && window.google.maps) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (res, status) => {
            try {
              if (status === "OK" && res && res[0]) {
                const comp = res[0].address_components || [];
                const city =
                  comp.find((c) => c.types.includes("administrative_area_level_2"))?.long_name ||
                  comp.find((c) => c.types.includes("locality"))?.long_name ||
                  "";
                const state = comp.find((c) => c.types.includes("administrative_area_level_1"))?.short_name || "";
                if (city && state) applyGeoToOriginIfEmpty(`${city} - ${state}`);
              }
            } finally {
              setIsGeoLoading(false);
            }
          });
        } else {
          setIsGeoLoading(false);
        }
      },
      (error) => {
        if (error?.code === 3) return tryLowAccuracyFallback();
        setIsGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 10000 }
    );
  };

  // ✅ Firestore listeners
  useEffect(() => {
    const unsubBanners = onSnapshot(
      query(collection(db, "banners"), orderBy("createdAt", "desc")),
      (snaps) => {
        const allRaw = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
        const all = allRaw
          .filter(isBannerActive)
          .filter((b) => !!getBannerImageUrl(b))
          .map((b) => ({
            ...b,
            imageUrl: getBannerImageUrl(b),
            placement: normalizePlacement(b.placement || "side"),
          }));

        const top = all.filter((b) => b.placement === "top");
        const leftExplicit = all.filter((b) => b.placement === "left");
        const rightExplicit = all.filter((b) => b.placement === "right");
        const genericSide = all.filter((b) => b.placement === "side");

        setBannersTop(top);

        if (genericSide.length === 1 && leftExplicit.length === 0 && rightExplicit.length === 0) {
          setBannersEsquerda(genericSide);
          setBannersDireita(genericSide);
        } else {
          const leftAlt = genericSide.filter((_, i) => i % 2 === 0);
          const rightAlt = genericSide.filter((_, i) => i % 2 !== 0);
          setBannersEsquerda([...leftExplicit, ...leftAlt]);
          setBannersDireita([...rightExplicit, ...rightAlt]);
        }
      },
      (err) => {
        console.error("Erro ao ouvir banners:", err);
        setBannersTop([]);
        setBannersEsquerda([]);
        setBannersDireita([]);
      }
    );

    const unsubRoutes = onSnapshot(collection(db, "routes"), (snaps) => {
      setAllRoutes(snaps.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    const unsubAdd = onSnapshot(
      query(collection(db, "addCidades"), orderBy("label", "asc")),
      (snaps) => {
        const items = snaps.docs
          .map((d) => d.data()?.label)
          .filter((v) => typeof v === "string" && v.trim().length > 0);
        setAddCidades(items);
      },
      (err) => {
        if (String(err?.code || "") === "permission-denied") {
          console.warn("Sem permissão para ler addCidades (rules):", err?.code);
        } else {
          console.error("Erro ao ouvir addCidades:", err);
        }
        setAddCidades([]);
      }
    );

    return () => {
      try { unsubBanners(); } catch {}
      try { unsubRoutes(); } catch {}
      try { unsubAdd(); } catch {}
    };
  }, []);

  const getLatLng = (cityName) => {
    return new Promise((resolve, reject) => {
      if (!window.google?.maps?.Geocoder) return reject("Indisponível");
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: cityName }, (res, status) => {
        if (status === "OK" && res?.[0]?.geometry?.location) resolve(res[0].geometry.location);
        else reject(status);
      });
    });
  };

  const clamp2 = (v) => Number(Number(v || 0).toFixed(2));

  const runSearch = async (forcedOrigin, forcedDest) => {
    const activeOrigin = forcedOrigin || origin;
    const activeDest = forcedDest || destination;

    if (!activeOrigin.trim() || !activeDest.trim()) return;

    const myId = ++searchIdRef.current;
    setLoading(true);

    try {
      const oriLL = await getLatLng(activeOrigin);
      const destLL = await getLatLng(activeDest);

      if (myId !== searchIdRef.current) return;

      const matched = [];

      for (const route of allRoutes) {
        let precoFinal = null;
        let distPercorrida = 0;

        if (route.pathPoints && route.cumKm) {
          const oriMin = minDistanceToPointsKm(route.pathPoints, oriLL);
          const destMin = minDistanceToPointsKm(route.pathPoints, destLL);

          if (oriMin <= ON_ROUTE_TOL_KM && destMin <= ON_ROUTE_TOL_KM) {
            const findIdx = (ll) => {
              let bI = -1;
              let bD = Infinity;
              route.pathPoints.forEach((p, i) => {
                const d = Math.pow(p.lat - ll.lat(), 2) + Math.pow(p.lng - ll.lng(), 2);
                if (d < bD) {
                  bD = d;
                  bI = i;
                }
              });
              return bI;
            };

            const idxO = findIdx(oriLL);
            const idxD = findIdx(destLL);

            if (idxO >= 0 && idxD >= 0 && idxO < idxD) {
              distPercorrida = Math.abs(route.cumKm[idxD] - route.cumKm[idxO]);

              const precoBase = Number(route.price || 0);
              const kmTotal = parseKm(route.distance) || 1;

              const fee = pickEmbarkFeeForSearchOrigin(route, activeOrigin) || 0;

              // ✅ 1) Override do trecho (aceita quebrado, ex: 27,50)
              const ov = pickOverrideFare(route, activeOrigin, activeDest);
              if (Number.isFinite(ov) && ov > 0) {
                precoFinal = clamp2(ov + fee);
              } else {
                // ✅ 2) Proporcional (sem arredondar para múltiplos de 5)
                const calculoBruto = (precoBase / kmTotal) * distPercorrida;

                let proporcional = clamp2(calculoBruto);
                if (proporcional < 15) proporcional = 15;

                let finalComTaxa = clamp2(proporcional + fee);

                // Se for quase a rota toda, cobra o preço cheio + taxa
                if (distPercorrida > kmTotal * 0.95) finalComTaxa = clamp2(precoBase + fee);

                // Nunca passa do preço máximo (preço cheio + taxa)
                const cap = precoBase + fee;
                if (finalComTaxa > cap) finalComTaxa = clamp2(cap);

                precoFinal = finalComTaxa;
              }
            }
          }
        }

        if (Number.isFinite(precoFinal) && precoFinal > 0) {
          matched.push({
            ...route,
            displayPrice: precoFinal,
            displayDistance: distPercorrida.toFixed(1),
          });
        }
      }

      setSearchMeta({
        origin: activeOrigin.split(",")[0].trim(),
        destination: activeDest.split(",")[0].trim(),
      });

      matched.sort((a, b) => timeToMinutes(a.outTime) - timeToMinutes(b.outTime));
      setResults(matched);
    } catch (e) {
      console.error(e);
      setResults([]);
    } finally {
      if (myId === searchIdRef.current) setLoading(false);
    }
  };

  const triggerAutoSearch = (newOrigin, newDest) => {
    if (newOrigin && newDest) setTimeout(() => runSearch(newOrigin, newDest), 350);
  };

  // ✅ Topo: carrossel horizontal (4 anúncios no desktop)
  const RenderTopCarousel = ({ data }) => {
    if (!data || data.length === 0) return null;

    return (
      <div className="av-top-carousel">
        <Swiper
          modules={[Autoplay]}
          autoplay={{ delay: 4500, disableOnInteraction: false }}
          loop={data.length > 4}
          slidesPerView={4}
          spaceBetween={14}
          className="av-top-swiper"
          breakpoints={{
            0: { slidesPerView: 1.15, spaceBetween: 10 },
            520: { slidesPerView: 2.15, spaceBetween: 12 },
            860: { slidesPerView: 3.15, spaceBetween: 12 },
            1100: { slidesPerView: 4, spaceBetween: 14 },
          }}
        >
          {data.map((b, i) => (
            <SwiperSlide key={`top-${b.id || "b"}-${i}`}>
              <a
                href={b.linkDestino || "#"}
                target="_blank"
                rel="noreferrer"
                className="av-top-banner-link"
                onClick={(e) => e.stopPropagation()}
              >
                <img src={b.imageUrl} alt="Publicidade" className="av-top-banner-img" />
              </a>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  };

  // ✅ Laterais: mantém fade
  const RenderSideSlider = ({ data, prefix }) => {
    if (!data || data.length === 0) return null;
    return (
      <Swiper
        modules={[Autoplay, EffectFade]}
        effect="fade"
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        loop={data.length > 1}
        className="av-swiper-container"
      >
        {data.map((b, i) => (
          <SwiperSlide key={`${prefix}-${b.id || "b"}-${i}`}>
            <a href={b.linkDestino || "#"} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
              <img src={b.imageUrl} alt="Publicidade" className="av-banner-img" />
            </a>
          </SwiperSlide>
        ))}
      </Swiper>
    );
  };

  return (
    <div className="av-app-container">
      {/* NAVBAR */}
      <nav className="av-navbar">
        <div className="av-nav-content">
          <div className="av-logo" onClick={() => window.location.reload()} style={{ cursor: "pointer" }}>
            <Bus size={30} color="var(--av-primary)" />
            <span>ACHEVAM</span>
          </div>

          <button className="av-menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>

          <div className="av-menu-desktop">
            <button className="av-nav-link" onClick={() => navigate("/sobre-nos")}>
              Sobre nós
            </button>

            <button className="av-nav-link" onClick={() => navigate("/login")}>
              Sou Empresa
            </button>
            <button className="av-btn-primary" onClick={() => navigate("/register")}>
              Trabalhe Conosco
            </button>
          </div>

          {menuOpen && (
            <div className="av-menu-mobile">
              <button className="av-nav-link" onClick={() => navigate("/login")}>
                Sou Empresa
              </button>
              <button className="av-btn-primary" onClick={() => navigate("/register")}>
                Trabalhe Conosco
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* TOPO DESKTOP */}
      <div className="av-top-carousel-desktop">
        <RenderTopCarousel data={bannersTop} />
      </div>

      {/* HERO + BUSCA */}
      <header className="av-hero">
        <div className="av-hero-title">
          <h1>Para onde vamos hoje?</h1>
          <p>Sua viagem por um preço justo e proporcional.</p>
        </div>

        <div className="av-search-card">
          <div className="av-search-wrapper">
            <div className="av-input-box">
              <Navigation
                size={20}
                className={isGeoLoading ? "animate-pulse" : origin ? "icon-active" : ""}
                onClick={handleGetLocation}
                style={{ cursor: "pointer" }}
              />
              <CityAutocomplete
                extraItems={addCidades}
                strict={true}
                onSelect={(val) => {
                  setOrigin(val);
                  triggerAutoSearch(val, destination);
                }}
                value={origin}
                placeholder="Origem"
              />
            </div>

            <ArrowLeftRight className="av-divider-icon" />

            <div className="av-input-box">
              <MapPin size={20} />
              <CityAutocomplete
                extraItems={addCidades}
                strict={true}
                onSelect={(val) => {
                  setDestination(val);
                  triggerAutoSearch(origin, val);
                }}
                value={destination}
                placeholder="Destino"
              />
            </div>

            <button className="av-search-btn" onClick={() => runSearch()} disabled={loading}>
              {loading ? <Loader2 className="animate-spin" /> : "Buscar"}
            </button>
          </div>
        </div>

        {/* MOBILE: anúncios aleatórios */}
        <div className="av-mobile-ads">
          <div className="av-mobile-ads-head">
            <span className="av-mobile-ads-title">Patrocinado</span>
            <span className="av-mobile-ads-sub">Empresas em destaque</span>
          </div>

          <div className="av-mobile-ads-row">
            {adsPool.slice(0, 10).map((b, idx) => (
              <a
                key={`${b?.id || "m"}-${idx}`}
                className="av-mobile-ad-card"
                href={b.linkDestino || "#"}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                title="Publicidade"
              >
                <img className="av-mobile-ad-img" src={b.imageUrl} alt="Publicidade" />
              </a>
            ))}
          </div>
        </div>
      </header>

      {/* CONTEÚDO PRINCIPAL */}
      <main className="av-main-layout">
        <aside className="av-side-column">
          <div className="av-sticky-content">
            <RenderSideSlider data={bannersEsquerda} prefix="left" />
          </div>
        </aside>

        <section className="av-center-content">
          <div className="av-routes-list">
            {loading && (
              <div className="av-loading-state">
                <Loader2 className="animate-spin" size={40} color="var(--av-primary)" />
                <p>Calculando melhores rotas...</p>
              </div>
            )}

            {!loading && results.length === 0 && origin && destination && (
              <p style={{ textAlign: "center", padding: "20px", color: "#64748b", fontWeight: 800 }}>
                Nenhuma van disponível para este trecho no momento.
              </p>
            )}

            {!loading &&
              results.map((r, index) => (
                <React.Fragment key={r.id}>
                  <div
                    className={`av-route-card animate-in ${expandedId === r.id ? "active" : ""}`}
                    onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                  >
                    <div className="av-route-main">
                      <div className="av-route-time">
                        <b>{r.outTime}</b>
                        <span>Saída</span>
                      </div>

                      <div className="av-route-info">
                        <span className="company">{r.companyName || "Van Particular"}</span>
                        <span className="av-path-text">
                          {searchMeta.origin} → {searchMeta.destination}
                        </span>
                        <div className="av-subroute-badge">Preço • {r.displayDistance}km</div>
                      </div>

                      <div className="av-route-price">
                        <small>R$</small>
                        {Number(r.displayPrice || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      </div>
                    </div>

                    {expandedId === r.id && (
                      <div className="av-expanded-actions">
                        {(() => {
                          const driver = getDriverPhone(r);
                          const company = pickCompanyPhoneForSearchOrigin(r, searchMeta.origin);

                          const waText = encodeURIComponent(
                            `Olá! Vi no Achevam a rota ${searchMeta.origin} para ${searchMeta.destination}. Gostaria de reservar minha vaga!`
                          );

                          return (
                            <>
                              <a
                                className="av-exp-btn av-exp-driver"
                                href={`https://wa.me/55${driver}?text=${waText}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                title="WhatsApp do motorista"
                              >
                                <Bus size={18} /> Motorista
                              </a>

                              {company ? (
                                <>
                                  <a
                                    className="av-exp-btn av-exp-company-wa"
                                    href={`https://wa.me/55${company}?text=${waText}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    title="WhatsApp da empresa (origem)"
                                  >
                                    <Building2 size={18} /> Empresa
                                  </a>

                                  <a
                                    className="av-exp-btn av-exp-company-call"
                                    href={`tel:+55${company}`}
                                    onClick={(e) => e.stopPropagation()}
                                    title="Ligar para a empresa (origem)"
                                  >
                                    <Phone size={18} /> Ligar
                                  </a>
                                </>
                              ) : null}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  {index > 0 && index % 3 === 0 && index < results.length - 2 && (
                    <div className="av-inline-ad">
                      {(() => {
                        if (adsPool.length === 0) return null;
                        const ad = adsPool[index % adsPool.length];
                        return (
                          <a
                            className="av-inline-ad-card"
                            href={ad.linkDestino || "#"}
                            target="_blank"
                            rel="noreferrer"
                            title="Patrocinado"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="av-inline-ad-badge">Patrocinado</span>
                            <img className="av-inline-ad-img" src={ad.imageUrl} alt="Publicidade" />
                          </a>
                        );
                      })()}
                    </div>
                  )}
                </React.Fragment>
              ))}
          </div>
        </section>

        <aside className="av-side-column">
          <div className="av-sticky-content">
            <RenderSideSlider data={bannersDireita} prefix="right" />
          </div>
        </aside>
      </main>
    </div>
  );
}
